import type {
  CountryCode,
  CreateOrderItemDTO,
  CreateOrderRequest,
  CreateOrderResponse,
  CustomerDTO,
  Language,
  OrderDTO,
  SveaCreateOrderModel,
  SveaOrder,
  SveaOrderRow,
  SveaPresetValue
} from 'ecosystem'
import { CustomerType } from 'ecosystem'
import { parseStoreToBackendOrderLines, paymentShippingFactory, sveaApi } from 'api'
import {
  createMinorUnit,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  getFullLocale,
  normalizeCountryCode,
  resolveBuyerCountry,
  resolveUrlPath
} from 'shared-utils'
import type { NextApiRequest, NextApiResponse } from 'next'
import storefrontApi from 'api/storefront-api'
import { sveaOrderLineFactory } from 'api/utils/svea/checkout'
import { type CreateOrderHandlerOpts } from './types'
import { ingridShippingProvider } from './ingridShippingProvider'
import { nshiftShippingProvider } from './nshiftShippingProvider'

const getSveaFullLocale = (locale: Language) => {
  return getFullLocale(locale, {
    no: 'nn-NO',
    en: 'en-US'
  })
}

/**
 * Markets where presetting the organisation number is known to work.
 *
 * Svea runs a per-country identity flow and only Sweden's accepts an
 * organisation number as a preset value. Poland rejects every value tried — a
 * Swedish orgnr, and checksum-valid Polish NIPs alike — failing the whole
 * create-order call with an empty-bodied 500 rather than a field error.
 *
 * This gates the number only. `IsCompany` is safe everywhere and is still
 * preset for business customers, so a B2B buyer lands in the company flow and
 * fills in their own local identifier there.
 *
 * Add a country here only once a B2B order has actually been placed in it.
 */
const ORG_NUMBER_PRESET_COUNTRIES = ['SE']

/**
 * The country the stored customer data belongs to, resolved the same way the
 * postal code below is: the customer's own addresses first, then the company's.
 */
const resolveCustomerCountry = (customer: CustomerDTO): string | undefined => {
  const customerAddresses = customer.addresses ?? []
  const country =
    customerAddresses.find((a) => a.main && a.country)?.country ??
    customerAddresses.find((a) => a.country)?.country ??
    resolveBuyerCountry(customer.companyProfile)

  return country ? normalizeCountryCode(country) : undefined
}

// Lookup order: customer.addresses (main first) -> companyProfile.addresses
// (default billing, then other billing, then shipping).
const resolveCustomerPostalCode = (customer: CustomerDTO): string | undefined => {
  const customerAddresses = customer.addresses ?? []
  const mainCustomerAddress = customerAddresses.find((a) => a.main && a.postalCode)
  if (mainCustomerAddress?.postalCode) return mainCustomerAddress.postalCode
  const anyCustomerAddress = customerAddresses.find((a) => a.postalCode)
  if (anyCustomerAddress?.postalCode) return anyCustomerAddress.postalCode

  const companyAddresses = customer.companyProfile?.addresses ?? []
  const billing = companyAddresses.filter((a) => a.type === 'BILLING' && a.postalCode)
  const defaultBilling = billing.find((a) => a.default)
  if (defaultBilling?.postalCode) return defaultBilling.postalCode
  if (billing[0]?.postalCode) return billing[0].postalCode

  const shipping = companyAddresses.find((a) => a.type === 'SHIPPING' && a.postalCode)
  return shipping?.postalCode
}

/**
 * The country the Svea session is created for: the shopper's pick when the
 * merchant offers a choice and the pick is allowed, otherwise the configured
 * country. Svea renders a different identity flow per country, so this is also
 * the delivery country the DEFAULT shipping rules are evaluated against.
 * @see https://paymentsdocs.svea.com/docs/checkout/checkout-business-flows/
 */
const resolveSveaCountry = (
  opts: CreateOrderHandlerOpts,
  requestedCountryCode: CountryCode | undefined
): string | undefined => {
  const { availableCountries, countryCode: configCountryCode } =
    opts.paymentProviderConfig?.providerOptions?.svea ?? {}

  if (
    requestedCountryCode &&
    (!availableCountries?.length || availableCountries.includes(requestedCountryCode))
  ) {
    return requestedCountryCode
  }

  return configCountryCode
}

const generateCreateSveaOrderPayload = async ({
  opts,
  locale,
  cartDiscountCode,
  cartStateUuid,
  items,
  backendOrder,
  sessionCountryCode
}: Pick<CreateOrderRequest, 'cartDiscountCode' | 'items' | 'cartStateUuid'> & {
  locale: Language
  opts: CreateOrderHandlerOpts
  backendOrder: OrderDTO
  /** The shopper's country pick, already validated by `resolveSveaCountry`. */
  sessionCountryCode?: string
}) => {
  // VAT stays tied to the merchant's configured country regardless of the
  // shopper's pick — the same split Qliro makes.
  const countryCode = opts.paymentProviderConfig?.providerOptions?.svea?.countryCode
  const { orderLines } = sveaOrderLineFactory(items, cartDiscountCode, countryCode, opts.buyerVat)

  const presetValues: SveaPresetValue[] = []

  if (opts.customerId) {
    try {
      const customer = await storefrontApi.customer.findById(opts.customerId)

      // Prefill email — non-readonly so the shopper can still change it
      // (they may be ordering on someone else's behalf).
      if (customer?.email) {
        presetValues.push({
          TypeName: 'EmailAddress',
          Value: customer.email
        })
      }

      if (customer?.phones[0]?.number) {
        presetValues.push({
          TypeName: 'PhoneNumber',
          Value: customer.phones[0].number
        })
      }

      // Svea runs a per-country identity flow and validates presets against the
      // session's country, so stored data only goes in when it belongs to that
      // country. Email and phone are country-neutral and always sent.
      const customerCountry = customer ? resolveCustomerCountry(customer) : undefined
      const presetsMatchSessionCountry =
        !!customerCountry &&
        !!sessionCountryCode &&
        customerCountry === normalizeCountryCode(sessionCountryCode)

      if (customer && presetsMatchSessionCountry) {
        const postalCode = resolveCustomerPostalCode(customer)
        if (postalCode) {
          presetValues.push({
            TypeName: 'PostalCode',
            Value: postalCode
          })
        }
      }

      const canPresetOrgNumber =
        presetsMatchSessionCountry &&
        !!customerCountry &&
        ORG_NUMBER_PRESET_COUNTRIES.includes(customerCountry)

      if (customer?.type === CustomerType.BUSINESS) {
        // Safe in every market: it only opens the company flow, whose
        // identifier field the shopper can still fill in themselves.
        presetValues.push({
          TypeName: 'IsCompany',
          Value: 'True',
          IsReadonly: true
        })

        // For B2B in Svea SCO, `NationalId` is the organisation number
        // (Svea reuses the same preset field for both consumer SSN and
        // company orgnr; `IsCompany=true` switches the meaning).
        const orgNumber = customer.companyProfile?.organizationNumber
        if (orgNumber && canPresetOrgNumber) {
          presetValues.push({
            TypeName: 'NationalId',
            Value: orgNumber
          })
        }
      }
    } catch {
      // Non-fatal: if the customer lookup fails, fall back to Svea's default
      // (the shopper can still toggle "Företag" themselves in the iframe).
    }
  }

  // Svea's ConfirmationUri does NOT support a template variable (only PushUri does, with
  // `{checkout.order.uri}` → Svea OrderId). To still get an identifier on the redirect back, we
  // hardcode the SF orderNumber. The confirmation page reads it as `order_id` and the SF order's
  // persisted `paymentId` (set in the handlers below right after createOrder) provides the link
  // back to the Svea OrderId.
  const confirmationUri = `${resolveUrlPath(opts.redirectDomain, '/confirmation')}?order_id=${encodeURIComponent(backendOrder.orderNumber ?? '')}`

  return {
    locale: getSveaFullLocale(locale),
    countryCode: sessionCountryCode || countryCode || DEFAULT_COUNTRY,
    currency:
      opts.paymentProviderConfig?.providerOptions?.svea?.purchaseCurrency || DEFAULT_CURRENCY,
    clientOrderNumber: backendOrder.orderNumber ?? '',
    merchantSettings: {
      TermsUri: resolveUrlPath(
        opts.redirectDomain,
        opts.paymentProviderConfig?.providerOptions?.svea?.termsUrl ?? '/terms'
      ),
      CheckoutUri: resolveUrlPath(opts.redirectDomain, '/checkout'),
      ConfirmationUri: confirmationUri,
      PushUri: resolveUrlPath(opts.redirectDomain, '/api/orders/push?order_id={checkout.order.uri}')
    },
    cart: {
      Items: orderLines
    },
    merchantData: JSON.stringify({
      cartStateUuid
    }),
    partnerKey: process.env.SVEA_PARTNER_KEY ?? '',
    ...(presetValues.length > 0 && { presetValues })
  } satisfies SveaCreateOrderModel
}

// Persist the Svea OrderId on the SF order. Required because Svea's ConfirmationUri can't carry
// a template variable: the confirmation page only knows the SF orderNumber and resolves to Svea
// via this paymentId. Failure here aborts the create-order request — leaving the SF order
// without a payment link would strand both records.
//
// PUT /storefront/orders is a full replace (not a merge). The create response only echoes a
// minimal projection (no items / customer), so we pass the already-prepared `backendItems`
// (the same CreateOrderItemDTO[] sent on create) through to the update. `id` is stripped
// because the BE matches by orderNumber.
const persistSveaPaymentId = async (
  backendOrder: OrderDTO,
  sveaOrder: SveaOrder,
  backendItems: CreateOrderItemDTO[]
) => {
  if (!backendOrder.orderNumber) {
    throw new Error('SF order is missing orderNumber — cannot persist Svea paymentId')
  }

  const { id: _id, customer: _customer, items: _items, ...rest } = backendOrder

  await storefrontApi.orders.updateStorefrontOrder({
    ...rest,
    items: backendItems,
    paymentId: String(sveaOrder.OrderId)
  })
}

const simpleShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  // VAT follows the merchant's configured country; the rules follow the
  // session country the shopper picked.
  const countryCode = opts.paymentProviderConfig?.providerOptions?.svea?.countryCode
  const sessionCountryCode = resolveSveaCountry(opts, requestedCountryCode)

  const { orderAmount, netOrderAmount } = sveaOrderLineFactory(
    items,
    cartDiscountCode,
    countryCode,
    opts.buyerVat
  )
  const shippingInfo = paymentShippingFactory({
    orderAmount,
    netOrderAmount,
    locale,
    opts: opts.shippingProviderConfig?.simpleShipping,
    shippingConfig: opts.shippingProviderConfig,
    customerType: opts.customerType,
    // A Svea session is created per market, so its purchase country is also
    // the delivery country — same assumption `PaymentSveaDefault` makes.
    // A Svea session is created for one country and the shopper cannot enter a
    // delivery address outside it, so the session country is the recipient's —
    // the shopper's own pick when the merchant offers a country selector.
    recipientCountryCode: sessionCountryCode,
    countryCode,
    buyerVat: opts.buyerVat
  })

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'SVEA'
    }
  })

  const sveaPayload = await generateCreateSveaOrderPayload({
    sessionCountryCode: resolveSveaCountry(opts, requestedCountryCode),
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale
  })

  const { error, data } = await sveaApi.checkout.createOrder({
    ...sveaPayload,
    cart: {
      Items: [
        ...sveaPayload.cart.Items,
        {
          ArticleNumber: shippingInfo.id,
          Name: shippingInfo.name,
          Quantity: createMinorUnit(1),
          Unit: 'NA',
          UnitPrice: shippingInfo.price,
          VatPercent: shippingInfo.tax_rate,
          RowType: 'ShippingFee'
        } satisfies SveaOrderRow
      ]
    }
  })

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  await persistSveaPaymentId(backendOrder, data, backendOrderLines)

  res.status(200).json({ storefrontOrderResponse: backendOrder, sveaResponse: data })
}

const postnordShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'SVEA'
    },
    shipping: {
      provider: 'POSTNORD'
    }
  })

  const sveaPayload = await generateCreateSveaOrderPayload({
    sessionCountryCode: resolveSveaCountry(opts, requestedCountryCode),
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale
  })

  const { error, data } = await sveaApi.checkout.createOrder(sveaPayload)

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  await persistSveaPaymentId(backendOrder, data, backendOrderLines)

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    sveaResponse: data
  })
}

const ingridShipping = (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  void ingridShippingProvider(req, res, opts, async (ingridResponse) => {
    const {
      storeId,
      items,
      cartDiscountCode,
      cartStateUuid,
      countryCode: requestedCountryCode
    }: CreateOrderRequest = JSON.parse(req.body)
    const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    const locale = opts.locale || localeForCredentials

    const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
    const backendOrder = await storefrontApi.orders.createStorefrontOrder({
      storeId,
      backendOrderLines,
      cartDiscountCode,
      customerId: opts.customerId,
      paymentProviderInfo: {
        providerName: 'SVEA'
      },
      shipping: {
        provider: 'INGRID',
        providerSessionId: ingridResponse.session.checkout_session_id
      }
    })

    const sveaPayload = await generateCreateSveaOrderPayload({
      sessionCountryCode: resolveSveaCountry(opts, requestedCountryCode),
      opts,
      backendOrder,
      items,
      cartDiscountCode,
      cartStateUuid,
      locale
    })

    const { error, data } = await sveaApi.checkout.createOrder(sveaPayload)

    if (error || !data) {
      res.status(400).json({ error: error || 'Unknown error' })
      return
    }

    await persistSveaPaymentId(backendOrder, data, backendOrderLines)

    res.status(200).json({
      storefrontOrderResponse: backendOrder,
      sveaResponse: data,
      ingridResponse
    })
  })
}

const nshiftShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const nshiftResponse = await nshiftShippingProvider(req, opts)

  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'SVEA'
    },
    shipping: {
      provider: 'NSHIFT',
      providerSessionId: nshiftResponse.sessionId
    }
  })

  const sveaPayload = await generateCreateSveaOrderPayload({
    sessionCountryCode: resolveSveaCountry(opts, requestedCountryCode),
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale
  })

  const { error, data } = await sveaApi.checkout.createOrder(sveaPayload)

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  await persistSveaPaymentId(backendOrder, data, backendOrderLines)

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    sveaResponse: data,
    nshiftResponse
  })
}

export const sveaPaymentProvider = async (
  req: NextApiRequest,
  res: NextApiResponse,
  opts: CreateOrderHandlerOpts
) => {
  const { shippingProviderConfig } = opts

  switch (shippingProviderConfig?.provider ?? 'DEFAULT') {
    case 'INGRID':
      return ingridShipping(req, res, opts)
    case 'POSTNORD':
      return postnordShipping(req, res, opts)
    case 'NSHIFT':
      return nshiftShipping(req, res, opts)
    case 'DEFAULT':
      return simpleShipping(req, res, opts)
  }
}
